import * as React from 'react';
import { useTranslation } from 'react-i18next';
import { Tag } from 'primereact/tag';
import {
    getCompletions,
    getSignatureHelp,
    validateExpression,
    CompletionItemKind,
} from '@utils/expressions/completion';

import './ExpressionEditor.scss';

export interface ExpressionEditorProps {
    value: string;
    onChange: (value: string) => void;
    onBlur?: (value: string) => void;
    placeholder?: string;
    disabled?: boolean;
    showValidation?: boolean;
    className?: string;
    height?: number | string;
    minimap?: boolean;
    lineNumbers?: 'on' | 'off';
    folding?: boolean;
}

export function ExpressionEditor({
    value = '',
    onChange,
    onBlur,
    placeholder,
    disabled = false,
    showValidation = true,
    className = '',
    height = '120px',
    minimap = false,
    lineNumbers = 'off',
    folding = false,
}: ExpressionEditorProps) {
    const { t } = useTranslation();
    const editorRef = React.useRef<any>(null);
    const containerRef = React.useRef<HTMLDivElement>(null);
    const completionProviderRef = React.useRef<any>(null);
    const signatureHelpProviderRef = React.useRef<any>(null);
    const [validation, setValidation] = React.useState(validateExpression(value));
    const [isEditorReady, setIsEditorReady] = React.useState(false);

    // Update validation when value changes
    React.useEffect(() => {
        setValidation(validateExpression(value));
    }, [value]);

    // Initialize Monaco editor
    React.useEffect(() => {
        if (!containerRef.current) return;

        // Load Monaco CSS if not already loaded
        if (!document.querySelector('link[href*="editor.main.css"]')) {
            const link = document.createElement('link');
            link.rel = 'stylesheet';
            link.href = '/js/monaco/min/vs/editor/editor.main.css';
            document.head.appendChild(link);
        }

        // Load Monaco if not already loaded
        if (!(window as any).monaco) {
            const loadMonaco = () => {
                // Store existing define
                const existingDefine = (window as any).define;

                // Set up Monaco loader
                (window as any).require = { paths: { vs: '/js/monaco/min/vs' } };

                const createElement = (src: string) => {
                    const script = document.createElement('script');
                    script.src = src;
                    script.async = true;
                    document.head.appendChild(script);
                    return script;
                };

                createElement('/js/monaco/min/vs/loader.js');
                createElement('/js/monaco/min/vs/editor/editor.main.nls.js');
                const main = createElement('/js/monaco/min/vs/editor/editor.main.js');

                // Wait for Monaco to be ready
                main.onload = () => {
                    const checkMonaco = setInterval(() => {
                        if ((window as any).monaco) {
                            clearInterval(checkMonaco);
                            initializeEditor();
                        }
                    }, 50);
                };
            };

            loadMonaco();
        } else {
            initializeEditor();
        }

        return () => {
            if (editorRef.current) {
                editorRef.current.dispose();
                editorRef.current = null;
            }
            // Dispose completion and signature providers to prevent accumulation
            if (completionProviderRef.current) {
                completionProviderRef.current.dispose();
                completionProviderRef.current = null;
            }
            if (signatureHelpProviderRef.current) {
                signatureHelpProviderRef.current.dispose();
                signatureHelpProviderRef.current = null;
            }
        };
    }, []);

    const initializeEditor = () => {
        const monaco = (window as any).monaco;

        if (!containerRef.current || editorRef.current) return;

        // Register the expression language
        monaco.languages.register({ id: 'expression' });

        editorRef.current = monaco.editor.create(containerRef.current, {
            value: value,
            language: 'expression',
            theme: 'vs',
            height: typeof height === 'number' ? height : undefined,
            automaticLayout: true,
            minimap: { enabled: minimap },
            lineNumbers: lineNumbers,
            folding: folding,
            scrollBeyondLastLine: false,
            fontSize: 13,
            fontFamily: 'Courier New, monospace',
            wordWrap: 'on',
            overviewRulerLanes: 0,
            glyphMargin: false,
            renderLineHighlight: 'none',
            hideCursorInOverviewRuler: true,
            quickSuggestions: {
                other: true,
                comments: false,
                strings: false,
            },
            suggestOnTriggerCharacters: true,
            acceptSuggestionOnEnter: 'on',
            tabCompletion: 'on',
        });

        // Register expression language completion provider
        completionProviderRef.current = monaco.languages.registerCompletionItemProvider('expression', {
            triggerCharacters: ['.', '(', ',', ' ', '&', '|', '=', '!', '<', '>', '"', "'", '/'],
            provideCompletionItems: (model: any, position: any) => {
                const text = model.getValue();
                const offset = model.getOffsetAt(position);
                const suggestions = getCompletions(text, offset);

                // Get the word after the last dot (what we're actually completing)
                const fullWord = getCurrentWord(model, offset);
                const wordAfterDot = fullWord.includes('.')
                    ? fullWord.substring(fullWord.lastIndexOf('.') + 1)
                    : fullWord;

                const startColumn = position.column - wordAfterDot.length;

                return {
                    suggestions: suggestions.map((s: any, index: number) => ({
                        label: s.label,
                        kind: getMonacoCompletionItemKind(s.kind),
                        detail: s.detail,
                        documentation: s.documentation,
                        insertText: (s.insertText || s.label).replace(/\$\{\d+\}/g, ''),
                        insertTextRules: monaco.languages.CompletionItemInsertTextRule.InsertAsSnippet,
                        range: {
                            startLineNumber: position.lineNumber,
                            startColumn: startColumn,
                            endLineNumber: position.lineNumber,
                            endColumn: position.column,
                        },
                        sortText: String.fromCharCode(97 + index), // a, b, c...
                    })),
                };
            },
        });

        // Register signature help provider
        signatureHelpProviderRef.current = monaco.languages.registerSignatureHelpProvider('expression', {
            signatureHelpTriggerCharacters: ['(', ','],
            provideSignatureHelp: (model: any, position: any) => {
                const text = model.getValue();
                const offset = model.getOffsetAt(position);
                const signature = getSignatureHelp(text, offset);

                if (!signature) {
                    return { value: null, dispose: () => {} };
                }

                return {
                    value: {
                        signatures: signature.signatures.map((s: any) => ({
                            label: s.label,
                            parameters: s.parameters.map((p: any) => ({
                                label: p.label,
                                documentation: p.documentation,
                            })),
                        })),
                        activeSignature: signature.activeSignature,
                        activeParameter: signature.activeParameter,
                    },
                    dispose: () => {},
                };
            },
        });

        // Add keyboard shortcut to manually trigger suggestions (Ctrl+Space)
        editorRef.current.addCommand(
            monaco.KeyMod.CtrlCmd | monaco.KeyCode.Space,
            () => {
                editorRef.current.trigger('keyboard', 'editor.action.triggerSuggest', {});
            }
        );

        // Listen for changes
        editorRef.current.onDidChangeModelContent(() => {
            const newValue = editorRef.current.getValue();
            onChange(newValue);
        });

        // Listen for blur
        editorRef.current.onDidBlurEditorText(() => {
            if (onBlur) {
                onBlur(editorRef.current.getValue());
            }
        });

        setIsEditorReady(true);
    };

    // Update editor value when prop changes (and editor exists)
    React.useEffect(() => {
        if (editorRef.current && value !== editorRef.current.getValue()) {
            editorRef.current.setValue(value);
        }
    }, [value]);

    const validationClass = showValidation && !validation.isValid ? 'p-invalid' : '';

    return (
        <div className={`ExpressionEditor Monaco ${className}`}>
            <div
                ref={containerRef}
                className={`monaco-editor-container ${validationClass}`}
                style={{ height: typeof height === 'number' ? `${height}px` : height }}
            />
            {showValidation && (
                <div className="validation-messages">
                    {!validation.isValid && validation.errors.length > 0 && (
                        <Tag
                            value={`Error: ${validation.errors[0]}`}
                            severity="danger"
                            icon="pi pi-exclamation-circle"
                        />
                    )}
                    {validation.warnings.length > 0 && (
                        <Tag
                            value={`Warning: ${validation.warnings[0]}`}
                            severity="warning"
                            icon="pi pi-exclamation-triangle"
                        />
                    )}
                </div>
            )}
        </div>
    );
}

function getMonacoCompletionItemKind(kind: CompletionItemKind): number {
    // Monaco's CompletionItemKind enum values (hardcoded to avoid undefined errors)
    // https://microsoft.github.io/monaco-editor/api/enums/monaco.languages.CompletionItemKind.html
    const MonacoKind = {
        Text: 0,
        Method: 1,
        Function: 2,
        Constructor: 3,
        Field: 4,
        Variable: 5,
        Class: 6,
        Interface: 7,
        Module: 8,
        Property: 9,
        Unit: 10,
        Value: 11,
        Enum: 12,
        Keyword: 13,
        Snippet: 14,
        Color: 15,
        File: 16,
        Reference: 17,
        Folder: 18,
        EnumMember: 19,
        Constant: 20,
        Struct: 21,
        Event: 22,
        Operator: 23,
        TypeParameter: 24,
    };

    switch (kind) {
        case 'function':
            return MonacoKind.Function;
        case 'method':
            return MonacoKind.Method;
        case 'operator':
            return MonacoKind.Operator;
        case 'property':
            return MonacoKind.Property;
        case 'variable':
            return MonacoKind.Variable;
        case 'keyword':
            return MonacoKind.Keyword;
        default:
            return MonacoKind.Text;
    }
}

// Helper to get current word at position
function getCurrentWord(model: any, offset: number): string {
    const text = model.getValue();
    const beforeCursor = text.substring(0, offset);
    const match = beforeCursor.match(/[\w.]+$/);
    return match ? match[0] : '';
}

export default ExpressionEditor;
